<template>
  <nav
    data-testid="main-navbar"
    class="sticky flex top-0 z-30 w-full h-10 shadow-lg px-4 justify-center bg-slate-800 text-gray-50"
  >
    <div class="flex relative w-full">
      <!-- 왼쪽: 로고/제목 -->
      <div class="flex items-center z-10">
        <a
          href="https://locktree.pro/"
          class="flex items-center gap-1.5 px-2 md:px-4 text-base md:text-lg font-semibold hover:text-blue-200 transition-colors"
        >
          <img src="/favicon.png" alt="Locktree" class="w-5 h-5 invert" />
          Locktree
        </a>
      </div>

      <!-- 모바일: 햄버거 메뉴 -->
      <div class="flex md:hidden ml-auto items-center z-10 gap-1">
        <template v-if="authStore.isAuthenticated">
          <button
            data-testid="nav-settings-button"
            class="p-2 text-sm bg-slate-700 hover:bg-slate-600 rounded transition-colors tap-target"
            :title="t('nav.settings')"
            @click="router.push({ name: 'settings' })"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>
        </template>
        <button
          class="p-2 hover:bg-slate-700 rounded transition-colors tap-target"
          :aria-label="mobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <Bars3Icon v-if="!mobileMenuOpen" class="w-6 h-6" />
          <XMarkIcon v-else class="w-6 h-6" />
        </button>
      </div>

      <!-- 데스크톱: 오른쪽 버튼 -->
      <div
        class="hidden md:flex ml-auto h-full items-center z-10 gap-2 bg-slate-800 pl-4"
      >
        <LocaleToggle test-id-prefix="nav-locale" />
        <template v-if="authStore.isAuthenticated">
          <span
            class="hidden min-[1400px]:block text-sm text-gray-300 px-2 max-w-[160px] truncate"
            :title="authStore.user?.email"
          >
            {{ authStore.user?.email }}
          </span>
          <button
            data-testid="nav-settings-button"
            class="px-3 py-1 text-sm bg-slate-700 hover:bg-slate-600 rounded transition-colors"
            @click="router.push({ name: 'settings' })"
          >
            {{ t("nav.settings") }}
          </button>
          <button
            class="px-3 py-1 text-sm bg-slate-700 hover:bg-slate-600 rounded transition-colors"
            @click="handleLogout"
          >
            {{ t("nav.logout") }}
          </button>
        </template>
        <template v-else>
          <button
            class="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-500 rounded transition-colors"
            @click="$emit('show-login')"
          >
            {{ t("nav.signIn") }}
          </button>
        </template>
      </div>

      <!-- 데스크톱: 중앙 내비게이션 -->
      <div
        class="hidden md:flex absolute w-full h-full left-0 top-0 gap-3 justify-center z-0"
      >
        <button
          :title="t('nav.solver')"
          data-testid="nav-solver-button"
          :class="
            'flex relative md:w-auto md:px-3 min-[1280px]:w-32 min-[1280px]:px-0 items-center justify-center font-semibold ' +
            'transition-colors hover:bg-slate-700 hover:text-blue-200 ' +
            (store.navView === 'solver' ? 'bg-slate-700 text-blue-200' : '')
          "
          @click="router.push({ name: 'solver' })"
        >
          <ComputerDesktopIcon class="w-6 h-6" />
          <span class="sr-only min-[1280px]:not-sr-only min-[1280px]:pl-3">{{
            t("nav.solver")
          }}</span>
        </button>
        <button
          :title="t('nav.aiSetup')"
          data-testid="nav-ai-setup-button"
          :class="
            'flex relative md:w-auto md:px-3 min-[1280px]:w-32 min-[1280px]:px-0 items-center justify-center font-semibold ' +
            'transition-colors hover:bg-slate-700 hover:text-blue-200 ' +
            (store.navView === 'ai-setup' ? 'bg-slate-700 text-blue-200' : '')
          "
          @click="router.push({ name: 'ai-setup' })"
        >
          <SparklesIcon class="w-6 h-6" />
          <span class="sr-only min-[1280px]:not-sr-only min-[1280px]:pl-3">{{
            t("nav.aiSetup")
          }}</span>
        </button>
        <button
          :title="t('nav.history')"
          data-testid="nav-history-button"
          :class="
            'flex relative md:w-auto md:px-3 min-[1280px]:w-32 min-[1280px]:px-0 items-center justify-center font-semibold ' +
            'transition-colors hover:bg-slate-700 hover:text-blue-200 ' +
            (store.navView === 'solve-history'
              ? 'bg-slate-700 text-blue-200'
              : '')
          "
          @click="router.push({ name: 'history' })"
        >
          <ClockIcon class="w-6 h-6" />
          <span class="sr-only min-[1280px]:not-sr-only min-[1280px]:pl-3">{{
            t("nav.history")
          }}</span>
        </button>
        <button
          :title="t('nav.solutions')"
          :class="
            'flex relative md:w-auto md:px-3 min-[1280px]:w-32 min-[1280px]:px-0 items-center justify-center font-semibold ' +
            'transition-colors hover:bg-slate-700 hover:text-blue-200 ' +
            (store.navView === 'preflop-solutions'
              ? 'bg-slate-700 text-blue-200'
              : '')
          "
          @click="router.push({ name: 'preflop-solutions' })"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 10h16M4 14h16M4 18h16"
            />
          </svg>
          <span class="sr-only min-[1280px]:not-sr-only min-[1280px]:pl-3">{{
            t("nav.solutions")
          }}</span>
        </button>
      </div>
    </div>

    <!-- 모바일: 드롭다운 메뉴 -->
    <div
      v-if="mobileMenuOpen"
      class="md:hidden absolute top-10 left-0 w-full bg-slate-800 shadow-lg border-t border-slate-700 z-40"
    >
      <div class="flex flex-col">
        <button
          data-testid="nav-solver-button"
          :class="
            'flex items-center px-4 py-3 text-left transition-colors ' +
            'hover:bg-slate-700 border-b border-slate-700 tap-target ' +
            (store.navView === 'solver' ? 'bg-slate-700 text-blue-200' : '')
          "
          @click="handleNavClick('solver')"
        >
          <ComputerDesktopIcon class="w-6 h-6 mr-3" />
          <span class="font-semibold">{{ t("nav.solver") }}</span>
        </button>
        <button
          data-testid="nav-ai-setup-button"
          :class="
            'flex items-center px-4 py-3 text-left transition-colors ' +
            'hover:bg-slate-700 border-b border-slate-700 tap-target ' +
            (store.navView === 'ai-setup' ? 'bg-slate-700 text-blue-200' : '')
          "
          @click="handleNavClick('ai-setup')"
        >
          <SparklesIcon class="w-6 h-6 mr-3" />
          <span class="font-semibold">{{ t("nav.aiSetup") }}</span>
        </button>
        <button
          data-testid="nav-history-button"
          :class="
            'flex items-center px-4 py-3 text-left transition-colors ' +
            'hover:bg-slate-700 border-b border-slate-700 tap-target ' +
            (store.navView === 'solve-history'
              ? 'bg-slate-700 text-blue-200'
              : '')
          "
          @click="handleNavClick('solve-history')"
        >
          <ClockIcon class="w-6 h-6 mr-3" />
          <span class="font-semibold">{{ t("nav.history") }}</span>
        </button>
        <div
          class="flex items-center px-4 py-3 border-b border-slate-700"
          role="group"
          :aria-label="t('nav.language')"
        >
          <LanguageIcon class="w-6 h-6 mr-3" />
          <span class="font-semibold">{{ t("nav.language") }}</span>
          <LocaleToggle
            class="ml-auto"
            test-id-prefix="nav-locale-mobile"
            enlarged-tap-target
          />
        </div>
        <template v-if="authStore.isAuthenticated">
          <button
            class="flex items-center px-4 py-3 text-left transition-colors hover:bg-slate-700 border-b border-slate-700 tap-target"
            @click="handleMobileLogout"
          >
            <svg
              class="w-6 h-6 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span class="font-semibold">{{ t("nav.logout") }}</span>
          </button>
        </template>
        <template v-else>
          <button
            class="flex items-center px-4 py-3 text-left transition-colors hover:bg-slate-700 bg-blue-600 tap-target"
            @click="handleMobileSignIn"
          >
            <svg
              class="w-6 h-6 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
              />
            </svg>
            <span class="font-semibold">{{ t("nav.signIn") }}</span>
          </button>
        </template>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "../store";
import { useAuthStore } from "../auth-store";
import { useI18n } from "../i18n";
import LocaleToggle from "./LocaleToggle.vue";

import {
  ComputerDesktopIcon,
  ClockIcon,
  SparklesIcon,
  Bars3Icon,
  XMarkIcon,
  LanguageIcon,
} from "@heroicons/vue/24/solid";

export default defineComponent({
  components: {
    ComputerDesktopIcon,
    ClockIcon,
    SparklesIcon,
    Bars3Icon,
    XMarkIcon,
    LanguageIcon,
    LocaleToggle,
  },
  emits: ["show-login"],
  setup(_, { emit }) {
    const authStore = useAuthStore();
    const store = useStore();
    const router = useRouter();
    const mobileMenuOpen = ref(false);
    const { t } = useI18n();

    const handleLogout = async () => {
      await authStore.logout();
    };

    const handleNavClick = (view: string) => {
      // 뷰를 라우트 이름으로 매핑 (history 특수 케이스 처리)
      const routeName = view === "solve-history" ? "history" : view;
      router.push({ name: routeName });
      mobileMenuOpen.value = false;
    };

    const handleMobileLogout = async () => {
      mobileMenuOpen.value = false;
      await handleLogout();
    };

    const handleMobileSignIn = () => {
      mobileMenuOpen.value = false;
      emit("show-login");
    };

    return {
      store,
      authStore,
      router,
      handleLogout,
      mobileMenuOpen,
      handleNavClick,
      handleMobileLogout,
      handleMobileSignIn,
      t,
    };
  },
});
</script>
